"use client";

import { memo, useState } from "react";

import CardLayout from "@/components/common/cardLayout";
import ErrorMessage from "@/components/common/errorMessage";

import { NotificationCategory } from "../types";
import RecruiterActionsContent from "./components/RecruiterActionsContent";
import Skeleton from "./components/skeleton";
import type { RecruiterActionsPage } from "./query";
import { useRecruiterActions } from "./query";

const RecruiterActions = () => {
  const [category, setCategory] = useState<NotificationCategory>("ALL");
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useRecruiterActions(category, 10);

  if (isLoading) {
    return (
      <CardLayout
        containerClassName="!mt-0 md:!h-[468px]"
        className="flex h-full flex-col rounded-xl border border-[rgba(232,235,240,0.5)] bg-white !p-4 shadow-[0_10px_45px_0_rgba(0,0,0,0.03)]"
      >
        <Skeleton />
      </CardLayout>
    );
  }

  if (isError || !data) {
    return (
      <CardLayout
        containerClassName="!mt-0 !h-[468px]"
        className="flex h-full flex-col rounded-xl border border-[rgba(232,235,240,0.5)] bg-white !p-4 shadow-[0_10px_45px_0_rgba(0,0,0,0.03)]"
      >
        <ErrorMessage text="Failed to load recruiter actions data" />
      </CardLayout>
    );
  }

  const pages: RecruiterActionsPage[] = data.pages || [];
  const mergedNotifications =
    pages.flatMap((page: RecruiterActionsPage) => page.notifications || []) ||
    [];
  const actionsData = {
    success: pages[0]?.success ?? true,
    start: pages[0]?.start ?? 0,
    limit: pages[0]?.limit ?? 10,
    total: pages[0]?.total ?? mergedNotifications.length,
    notifications: mergedNotifications,
    categories:
      pages[0]?.categories ||
      ({} as Record<
        NotificationCategory,
        { count: number; notificationTypes?: string[] } | undefined
      >),
  };

  return (
    <CardLayout
      containerClassName="!mt-0 md:!h-[468px] h-[381px]"
      className="flex h-full flex-col rounded-xl border border-[rgba(232,235,240,0.5)] bg-white px-3 py-4 shadow-[0_10px_45px_0_rgba(0,0,0,0.03)] md:!p-4"
    >
      <RecruiterActionsContent
        data={actionsData}
        selectedCategory={category}
        onCategoryChange={setCategory}
        onLoadMore={() => fetchNextPage()}
        hasMore={Boolean(hasNextPage)}
        isFetchingMore={isFetchingNextPage}
      />
    </CardLayout>
  );
};

export default memo(RecruiterActions);
